import {
	effectScope,
	inject,
	nextTick,
	onUnmounted,
	provide,
	shallowRef,
	watch,
	type Ref,
} from "vue";
import { MapSymbol } from "../injection-symbols";
import { LibLoader } from "../lib-loader";

class MapPool {
	#id: string;
	#counter = 0;

	maps: google.maps.Map[] = [];

	constructor(id: string) {
		this.#id = id;
	}

	async #newMap(): Promise<google.maps.Map> {
		const { Map } = await LibLoader.loadMaps();

		const mapContainer = document.createElement("div");
		mapContainer.id = `map-${this.#id}-${this.#counter++}`;
		mapContainer.className = "gmap";
		mapContainer.style.width = "100%";
		mapContainer.style.height = "100%";

		const map = new Map(mapContainer, { mapId: this.#id, zoom: 5 });

		return map;
	}

	async get(): Promise<google.maps.Map> {
		if (this.maps.length > 0) {
			return this.maps.pop()!;
		} else {
			return this.#newMap();
		}
	}

	release(map: google.maps.Map) {
		map.getDiv()?.remove();
		this.maps.push(map);
	}
}

function createMapStore() {
	function factory() {
		const pools = new Map<string, MapPool>();

		return {
			async get(id: string): Promise<google.maps.Map> {
				let pool = pools.get(id);
				if (pool == null) {
					pool = new MapPool(id);
					pools.set(id, pool);
				}

				return pool.get();
			},

			release(id: string, map: google.maps.Map) {
				pools.get(id)?.release(map);
			},
		};
	}

	let initialized = false;
	const scope = effectScope(true);
	let state: ReturnType<typeof factory>;

	return () => {
		if (!initialized) {
			state = scope.run(() => factory())!;
			initialized = true;
		}

		return state!;
	};
}

const useMapStore = createMapStore();

// Init a new map or grab an existing one from the map pool
// The map is then provided to the children of the Map component
export function provideMap(id: Ref<string>) {
	const store = useMapStore();

	const map = shallowRef<google.maps.Map>();
	const exposedMap = shallowRef<google.maps.Map>();

	watch(
		id,
		async (id, prevId) => {
			if (prevId != null && map.value != null) {
				store.release(prevId, map.value);
			}

			// Wait for vue to call all onUnmounted hooks
			await nextTick();
			map.value = await store.get(id);
		},
		{ immediate: true }
	);

	provide(MapSymbol, exposedMap);

	onUnmounted(() => {
		if (map.value == null) return;

		store.release(id.value, map.value);
	});

	function isReady() {
		exposedMap.value = map.value;
	}

	return { map, isReady };
}

export function useMap() {
	const map = inject(MapSymbol);

	if (!map) {
		throw new Error("Object must be a child of the map");
	}

	return map;
}
